import React, { useState, useCallback, useEffect, useRef } from "react";
import { Play, Loader2, Eye, EyeOff, Copy, Check, Clock, Upload } from "lucide-react";

const API_SERVER_URL =
  process.env.REACT_APP_PUBLIC_API_URL || "https://apis-ping.blutec.ai";

const METHOD_COLORS = {
  GET: "bg-emerald-500",
  POST: "bg-blue-500",
  PUT: "bg-amber-500",
  DELETE: "bg-red-500",
};

const ApiPlayground = ({ endpoint }) => {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [body, setBody] = useState("");
  const [response, setResponse] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [responseTime, setResponseTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formFields, setFormFields] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!endpoint) return;
    if (endpoint.isMultipart) {
      const fields = {};
      endpoint.requestFields?.filter((f) => f.type !== "file").forEach((f) => {
        fields[f.name] = "";
      });
      setFormFields(fields);
      setSelectedFile(null);
    } else if (endpoint.requestBody) {
      setBody(JSON.stringify(endpoint.requestBody, null, 2));
    } else {
      setBody("");
    }
    setResponse(null);
    setStatusCode(null);
    setResponseTime(null);
  }, [endpoint]);

  const handleSend = useCallback(async () => {
    if (!endpoint || !apiKey.trim()) return;
    setLoading(true);
    setResponse(null);
    setStatusCode(null);

    const start = performance.now();

    try {
      let url = `${API_SERVER_URL}${endpoint.path}`;
      const headers = { "X-API-Key": apiKey };
      let fetchOptions = { method: endpoint.method, headers };

      if (endpoint.method === "GET") {
        if (body.trim()) {
          try {
            const params = JSON.parse(body);
            const qs = new URLSearchParams(params).toString();
            if (qs) url += `?${qs}`;
          } catch { /* ignore */ }
        }
      } else if (endpoint.isMultipart) {
        const form = new FormData();
        Object.entries(formFields).forEach(([k, v]) => {
          if (v) form.append(k, v);
        });
        if (selectedFile) form.append("file", selectedFile);
        fetchOptions.body = form;
      } else {
        headers["Content-Type"] = "application/json";
        fetchOptions.headers = headers;
        fetchOptions.body = body;
      }

      fetchOptions.headers = headers;
      const res = await fetch(url, fetchOptions);
      const elapsed = Math.round(performance.now() - start);
      const data = await res.json();

      setStatusCode(res.status);
      setResponseTime(elapsed);
      setResponse(JSON.stringify(data, null, 2));
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      setResponseTime(elapsed);
      setStatusCode(0);
      setResponse(JSON.stringify({ error: "Network error", message: err.message }, null, 2));
    } finally {
      setLoading(false);
    }
  }, [endpoint, apiKey, body, formFields, selectedFile]);

  const handleCopyResponse = useCallback(() => {
    if (!response) return;
    navigator.clipboard.writeText(response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [response]);

  if (!endpoint) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm px-6 text-center">
        Select an API endpoint to try it here
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50/80">
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold text-white ${METHOD_COLORS[endpoint.method] || "bg-gray-500"}`}>
            {endpoint.method}
          </span>
          <code className="text-[11px] text-gray-600 font-mono truncate">{endpoint.path}</code>
        </div>

        <div className="relative">
          <input
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="pk_live_your_api_key"
            className="w-full px-3 py-2 pr-8 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white"
          />
          <button
            onClick={() => setShowKey(!showKey)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showKey ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1 block">
            {endpoint.isMultipart ? "Form Fields" : endpoint.method === "GET" ? "Query Parameters (JSON)" : "Request Body"}
          </label>

          {endpoint.isMultipart ? (
            <div className="space-y-2">
              {Object.keys(formFields).map((key) => (
                <div key={key}>
                  <label className="text-[10px] text-gray-500 mb-0.5 block font-mono">{key}</label>
                  <input
                    type="text"
                    value={formFields[key]}
                    onChange={(e) => setFormFields((prev) => ({ ...prev, [key]: e.target.value }))}
                    className="w-full px-2.5 py-1.5 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40"
                  />
                </div>
              ))}
              <div>
                <label className="text-[10px] text-gray-500 mb-0.5 block font-mono">file</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="w-full text-xs text-gray-500 file:mr-2 file:px-2 file:py-1 file:text-[10px] file:font-medium file:bg-white file:border file:border-gray-200 file:rounded-lg hover:file:bg-gray-50"
                />
                {selectedFile && (
                  <div className="flex items-center gap-1 mt-1 text-[10px] text-gray-400">
                    <Upload size={10} /> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
              </div>
            </div>
          ) : (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              spellCheck={false}
              className="w-full px-3 py-2.5 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#048AAD]/40 bg-white resize-none leading-relaxed"
            />
          )}
        </div>

        <button
          onClick={handleSend}
          disabled={loading || !apiKey.trim()}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold text-white bg-[#048AAD] hover:bg-[#037a99] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {loading ? "Sending..." : "Send Request"}
        </button>

        {response && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Response</span>
                {statusCode !== null && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    statusCode >= 200 && statusCode < 300
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-red-100 text-red-600"
                  }`}>
                    {statusCode || "ERR"}
                  </span>
                )}
                {responseTime !== null && (
                  <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                    <Clock size={10} /> {responseTime}ms
                  </span>
                )}
              </div>
              <button onClick={handleCopyResponse} className="p-1 rounded text-gray-400 hover:text-gray-600 transition-colors">
                {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
              </button>
            </div>
            <pre className="bg-[#0d1117] border border-gray-800 rounded-lg p-3 overflow-x-auto text-[12px] leading-relaxed text-white max-h-64 overflow-y-auto">
              {response}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiPlayground;
